import { useCallback, useContext, useEffect, useState } from 'react';

import {
  ApiGetResult,
  ApiResult,
  FetchApiCallback,
  useApiGet,
  useApiPost
} from '../../common/api/Api.hooks';
import { JwtToken } from '../../user/JwtToken.interface';
import { AuthorizeUrlDto, JiraLinkedEntities } from './jira-server.interface';
import { LoginContext } from '../../login/Login.context';
import { UserContext, UserContextState } from '../../user/User.context';
import api from '../../common/api/api';
import { User } from '../../user/User.interface';
import { JiraIssues } from '../jira-connect/JiraConnect.interface';
import { getFindJiraIssuesRequestOptions } from '../jira-connect/JiraConnect.hooks';
import {
  JiraServerRequestType,
  JiraServerUser,
  useJiraServerContext
} from './jira-server.context';

export function useJiraServerAuthUrl(): [
  ApiResult<AuthorizeUrlDto>,
  FetchApiCallback
] {
  const { get, ...result } = useApiGet<AuthorizeUrlDto>(
    'jira-server/authorize'
  );

  useEffect(() => {
    if (result.data) {
      console.log('setting cookies:', result.data);
      api.setCookie('jiraServerToken', result.data.token);
      api.setCookie('jiraServerTokenSecret', result.data.token_secret);
    }
  }, [result.data]);

  return [result, get];
}

export function useJiraServerLogin(): [
  ApiResult<User>,
  (code: string) => void
] {
  const { user, setUser } = useContext<UserContextState>(UserContext);
  const { setDidLogout } = useContext(LoginContext);
  const { data, isLoading, error, post } = useApiPost<[JwtToken, string]>(
    'jira-server/authorize'
  );

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const [jwt, jiraAccessToken] = data;
      api.setAccessToken(jwt.accessToken);
      setUser(jwt.user);
      api.setAccessToken(jiraAccessToken, 'jira');
      setDidLogout(false);
    }
  }, [data, setUser, setDidLogout]);

  const jiraLogin = useCallback(
    (code: string) => {
      const token = api.getCookie('jiraServerToken');
      const tokenSecret = api.getCookie('jiraServerTokenSecret');
      post({ code, token, tokenSecret });
    },
    [post]
  );
  return [{ data: user as User, isLoading, error }, jiraLogin];
}

export function useAuthorizeJiraServerPluginUser() {
  const { data, isLoading, error, post } = useApiPost<JwtToken>(
    'jira-server/authorize/plugin/user'
  );
  const { user, setUser } = useContext(UserContext);
  const { setDidLogout } = useContext(LoginContext);
  useEffect(() => {
    if (data) {
      const jwt = data;
      api.setAccessToken(jwt.accessToken);
      setUser(jwt.user);
      setDidLogout(false);
    }
  }, [data, setUser, setDidLogout]);
  const authorizeUser = useCallback(
    (jiraUser: JiraServerUser, token: string) => {
      post({ ...jiraUser, token });
    },
    [post]
  );
  return { user, isLoading, error, authorizeUser };
}

export function useFindJiraServerIssues() {
  const { request } = useJiraServerContext();
  const [jiraServerIssues, setJiraServerIssues] = useState<JiraIssues>();
  const [isLoading, setIsLoading] = useState(false);
  const findJiraServerIssues = useCallback(
    (search: string) => {
      const { data } = getFindJiraIssuesRequestOptions({
        limit: 100,
        title: search,
        sort: 'created',
        direction: 'DESC'
      });
      setIsLoading(true);
      request(JiraServerRequestType.Search, data, (results: JiraIssues) => {
        setIsLoading(false);
        setJiraServerIssues(results);
      });
    },
    [request]
  );
  return {
    jiraServerIssues,
    loadingJiraServerIssues: isLoading,
    findJiraServerIssues
  };
}

export function useFindLinkedEntities(
  jiraKey: string
): ApiGetResult<JiraLinkedEntities> {
  return useApiGet<JiraLinkedEntities>(`tasks-jira/tasks/${jiraKey}`);
}
